import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ArrowRight } from "@phosphor-icons/react";

function safeNext(raw) {
  // Only allow same-origin path redirects (must start with /, no //).
  if (!raw) return "/dashboard";
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
  } catch {
    /* noop */
  }
  return "/dashboard";
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = safeNext(params.get("next"));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(nextPath);
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto grid lg:grid-cols-2 border-b border-[var(--border)] min-h-[calc(100vh-4rem)]">
      <div className="hidden lg:block bg-black relative grain">
        <div className="absolute inset-0 flex items-end p-12 text-white">
          <div>
            <div className="micro-label text-white/60">/ Livery workshop</div>
            <h2 className="font-display font-black text-5xl mt-2 leading-[0.95]">
              Sign in to<br/>
              save your<br/>
              <span className="text-[var(--accent)]">decals.</span>
            </h2>
          </div>
        </div>
      </div>
      <div className="p-8 md:p-16 flex items-center">
        <form onSubmit={onSubmit} className="w-full max-w-sm mx-auto" data-testid="login-form">
          <div className="micro-label">/ Sign in</div>
          <h1 className="font-display font-black text-4xl mt-1">Welcome back</h1>
          <p className="text-[var(--fg-2)] text-sm mt-3">
            No account? <Link to={`/register${nextPath !== "/dashboard" ? `?next=${encodeURIComponent(nextPath)}` : ""}`} className="text-[var(--accent)] underline">Create one</Link>
          </p>
          <p className="text-[var(--fg-2)] text-sm mt-1">
            Forgot your password?{" "}
            <Link to="/reset-password" className="text-[var(--accent)] underline" data-testid="link-reset-password">
              Have a reset code
            </Link>
          </p>

          <label className="block mt-8">
            <span className="micro-label">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="input-email"
            />
          </label>
          <label className="block mt-4">
            <span className="micro-label">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--border)] bg-white font-mono text-sm focus:border-black focus:outline-none"
              data-testid="input-password"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="btn-accent w-full mt-8 inline-flex items-center justify-center gap-2"
            data-testid="btn-login-submit"
          >
            {loading ? "Signing in…" : (<>Sign in <ArrowRight size={14} weight="bold"/></>)}
          </button>
        </form>
      </div>
    </div>
  );
}
